import React from 'react';
import { KybSubmission } from '../../services/adminApiService';
import { API_BASE } from '../../constants/app.config';
import './AdminComponents.css';
import { FilePreviewModal } from '../common/FilePreviewModal';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`kyb-tabpanel-${index}`}
      aria-labelledby={`kyb-tab-${index}`}
      {...other}
    >
      {value === index && (
        <div className="kyb-tab-content">
          {children}
        </div>
      )}
    </div>
  );
}

interface KybDetailsModalProps {
  open: boolean;
  onClose: () => void;
  submission: KybSubmission | null;
}

const KybDetailsModal: React.FC<KybDetailsModalProps> = ({ open, onClose, submission }) => {
  const [tabValue, setTabValue] = React.useState(0);
  const [previewFile, setPreviewFile] = React.useState<{
    isOpen: boolean;
    url: string;
    fileName: string;
    contentType?: string;
  }>({
    isOpen: false,
    url: '',
    fileName: '',
    contentType: undefined
  });

  const handleTabChange = (newValue: number) => {
    setTabValue(newValue);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'APPROVED': return 'status-badge approved';
      case 'REJECTED': return 'status-badge rejected';
      case 'PENDING': return 'status-badge pending';
      case 'IN_PROGRESS': return 'status-badge in-progress';
      default: return 'status-badge';
    }
  };

  if (!open || !submission) return null;

  // Normalize possible JSON strings for directors/signatories coming from backend
  let directorsList: any[] = [];
  let signatoriesList: any[] = [];
  try {
    if (Array.isArray((submission as any).directors)) {
      directorsList = (submission as any).directors as any[];
    } else if (typeof (submission as any).directors === 'string' && (submission as any).directors.trim()) {
      directorsList = JSON.parse((submission as any).directors);
    }
  } catch {}
  try {
    if (Array.isArray((submission as any).signatories)) {
      signatoriesList = (submission as any).signatories as any[];
    } else if (typeof (submission as any).signatories === 'string' && (submission as any).signatories.trim()) {
      signatoriesList = JSON.parse((submission as any).signatories);
    }
  } catch {}

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content kyb-details-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>
        {/* Header */}
        <div className="modal-header">
          <div className="kyb-header-content">
            <div className="company-info">
              <div className="company-avatar">
                <span className="company-icon">🏢</span>
              </div>
              <div className="company-details">
                <h2>{submission.companyName}</h2>
                <p className="company-email">{submission.email}</p>
                <p className="company-type">Business Account</p>
              <span className={getStatusBadgeClass(submission.status)}>
                {submission.status}
              </span>
            </div>
          </div>
          </div>
        </div>

        {/* Tabs as cards */}
        <div className="tab-card-grid">
          <button className={tabValue === 0 ? 'tab-card active' : 'tab-card'} onClick={() => handleTabChange(0)}>
            <span className="tab-emoji">🏢</span>
            <div>
              <div className="tab-title">Basic Information</div>
              <div className="tab-sub">Company profile</div>
            </div>
          </button>
          <button className={tabValue === 1 ? 'tab-card active' : 'tab-card'} onClick={() => handleTabChange(1)}>
            <span className="tab-emoji">🧾</span>
            <div>
              <div className="tab-title">Business Details</div>
              <div className="tab-sub">Address, activity, turnover</div>
            </div>
          </button>
          <button className={tabValue === 2 ? 'tab-card active' : 'tab-card'} onClick={() => handleTabChange(2)}>
            <span className="tab-emoji">👥</span>
            <div>
              <div className="tab-title">Directors & Signatories</div>
              <div className="tab-sub">People & roles</div>
            </div>
          </button>
          <button className={tabValue === 3 ? 'tab-card active' : 'tab-card'} onClick={() => handleTabChange(3)}>
            <span className="tab-emoji">📂</span>
            <div>
              <div className="tab-title">Documents</div>
              <div className="tab-sub">Corporate documents</div>
            </div>
          </button>
          <button className={tabValue === 4 ? 'tab-card active' : 'tab-card'} onClick={() => handleTabChange(4)}>
            <span className="tab-emoji">🛡️</span>
            <div>
              <div className="tab-title">PEP & Sanctions</div>
              <div className="tab-sub">Screening results</div>
            </div>
          </button>
          <button className={tabValue === 5 ? 'tab-card active' : 'tab-card'} onClick={() => handleTabChange(5)}>
            <span className="tab-emoji">📝</span>
            <div>
              <div className="tab-title">Review</div>
              <div className="tab-sub">Status & notes</div>
            </div>
          </button>
        </div>

        {/* Content */}
        <div className="modal-body">
          <TabPanel value={tabValue} index={0}>
            <div className="kyb-info-section">
              <h3 style={{ fontWeight: 800 }}>Company Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label style={{ fontWeight: 700 }}>Company Name</label>
                  <span>{submission.companyName}</span>
                </div>
                <div className="info-item">
                  <label style={{ fontWeight: 700 }}>Registration Number</label>
                  <span>{submission.registrationNumber || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={{ fontWeight: 700 }}>Date of Incorporation</label>
                  <span>{submission.dateOfIncorporation ? submission.dateOfIncorporation : 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={{ fontWeight: 700 }}>Country of Incorporation</label>
                  <span>{submission.countryOfIncorporation || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={{ fontWeight: 700 }}>Business Type</label>
                  <span>{submission.businessType || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={{ fontWeight: 700 }}>Industry</label>
                  <span>{submission.industry || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={{ fontWeight: 700 }}>Website</label>
                  <span>{submission.website || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={{ fontWeight: 700 }}>Email</label>
                  <span>{submission.email}</span>
                </div>
                <div className="info-item">
                  <label style={{ fontWeight: 700 }}>Phone</label>
                  <span>{submission.phone || 'Not provided'}</span>
                </div>
              </div>
            </div>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <div className="kyb-info-section">
              <h3 style={{ fontWeight: 800 }}>Business Details</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label style={{ fontWeight: 700 }}>Business Address</label>
                  <span>{(submission as any).businessAddress || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={{ fontWeight: 700 }}>Business Activity</label>
                  <span>{submission.businessActivity || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={{ fontWeight: 700 }}>Expected Monthly Turnover</label>
                  <span>{submission.expectedMonthlyTurnover || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={{ fontWeight: 700 }}>Source of Funds</label>
                  <span>{submission.sourceOfFunds || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={{ fontWeight: 700 }}>Purpose of Account</label>
                  <span>{submission.purposeOfAccount || 'Not provided'}</span>
                </div>
              </div>
            </div>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <div className="kyb-info-section">
              <h3 style={{ fontWeight: 800 }}>Directors & Signatories</h3>
              <div className="directors-section">
                <h4 style={{ fontWeight: 800 }}>Directors</h4>
                <div className="directors-list">
                  {directorsList && directorsList.length > 0 ? (
                    directorsList.map((director, index) => (
                      <div key={index} className="director-item">
                        <div className="director-info">
                          <h5>{director.name}</h5>
                          <p>Position: {director.position}</p>
                          <p>Nationality: {director.nationality}</p>
                          <p>Shareholding: {director.shareholding}</p>
                          <p>Address: {director.address}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="no-data">No directors information provided</p>
                  )}
                </div>
              </div>
              
              <div className="signatories-section">
                <h4 style={{ fontWeight: 800 }}>Signatories</h4>
                <div className="signatories-list">
                  {signatoriesList && signatoriesList.length > 0 ? (
                    signatoriesList.map((signatory, index) => (
                      <div key={index} className="signatory-item">
                        <div className="signatory-info">
                          <h5>{signatory.name}</h5>
                          <p>Position: {signatory.position}</p>
                          <p>Authority: {signatory.authority}</p>
                          <p>Address: {signatory.address}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="no-data">No signatories information provided</p>
                  )}
                </div>
              </div>
            </div>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <div className="kyb-info-section">
              <h3 style={{ fontWeight: 800 }}>Submitted Documents</h3>
              <div className="documents-grid">
                <div className="document-item">
                  <div className="document-icon">📋</div>
                  <div className="document-info">
                    <h4>Certificate of Incorporation</h4>
                    <span className={`document-status ${submission.certificateOfIncorporationPath ? 'submitted' : 'missing'}`}>
                      {submission.certificateOfIncorporationPath ? '✅ Submitted' : '❌ Missing'}
                    </span>
                    {submission.certificateOfIncorporationPath && (
                      <button
                        onClick={() => setPreviewFile({
                          isOpen: true,
                          url: `${API_BASE}/admin/files/${submission.certificateOfIncorporationPath}`,
                          fileName: submission.certificateOfIncorporationPath?.split('/')?.pop() || 'Certificate of Incorporation',
                          contentType: 'application/pdf'
                        })}
                        className="document-link"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', font: 'inherit' }}
                      >
                        View
                      </button>
                    )}
                  </div>
                </div>

                <div className="document-item">
                  <div className="document-icon">📋</div>
                  <div className="document-info">
                    <h4>Memorandum of Association</h4>
                    <span className={`document-status ${submission.memorandumPath ? 'submitted' : 'missing'}`}>
                      {submission.memorandumPath ? '✅ Submitted' : '❌ Missing'}
                    </span>
                    {submission.memorandumPath && (
                      <button
                        onClick={() => setPreviewFile({
                          isOpen: true,
                          url: `${API_BASE}/admin/files/${submission.memorandumPath}`,
                          fileName: submission.memorandumPath?.split('/')?.pop() || 'Memorandum of Association',
                          contentType: 'application/pdf'
                        })}
                        className="document-link"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', font: 'inherit' }}
                      >
                        View
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="document-item">
                  <div className="document-icon">📜</div>
                  <div className="document-info">
                    <h4>Articles of Association</h4>
                    <span className={`document-status ${submission.articlesPath ? 'submitted' : 'missing'}`}>
                      {submission.articlesPath ? '✅ Submitted' : '❌ Missing'}
                    </span>
                    {submission.articlesPath && (
                      <button
                        onClick={() => setPreviewFile({
                          isOpen: true,
                          url: `${API_BASE}/admin/files/${submission.articlesPath}`,
                          fileName: submission.articlesPath?.split('/')?.pop() || 'Articles of Association',
                          contentType: 'application/pdf'
                        })}
                        className="document-link"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', font: 'inherit' }}
                      >
                        View
                      </button>
                    )}
                  </div>
                </div>

                <div className="document-item">
                  <div className="document-icon">📝</div>
                  <div className="document-info">
                    <h4>Board Resolution</h4>
                    <span className={`document-status ${submission.boardResolutionPath ? 'submitted' : 'missing'}`}>
                      {submission.boardResolutionPath ? '✅ Submitted' : '❌ Missing'}
                    </span>
                    {submission.boardResolutionPath && (
                      <button
                        onClick={() => setPreviewFile({
                          isOpen: true,
                          url: `${API_BASE}/admin/files/${submission.boardResolutionPath}`,
                          fileName: submission.boardResolutionPath?.split('/')?.pop() || 'Board Resolution',
                          contentType: 'application/pdf'
                        })}
                        className="document-link"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', font: 'inherit' }}
                      >
                        View
                      </button>
                    )}
                  </div>
                </div>

                <div className="document-item">
                  <div className="document-icon">👥</div>
                  <div className="document-info">
                    <h4>Beneficial Ownership Register</h4>
                    <span className={`document-status ${submission.beneficialOwnershipPath ? 'submitted' : 'missing'}`}>
                      {submission.beneficialOwnershipPath ? '✅ Submitted' : '❌ Missing'}
                    </span>
                    {submission.beneficialOwnershipPath && (
                      <button
                        onClick={() => setPreviewFile({
                          isOpen: true,
                          url: `${API_BASE}/admin/files/${submission.beneficialOwnershipPath}`,
                          fileName: submission.beneficialOwnershipPath?.split('/')?.pop() || 'Beneficial Ownership Register',
                          contentType: 'application/pdf'
                        })}
                        className="document-link"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', font: 'inherit' }}
                      >
                        View
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="document-item">
                  <div className="document-icon">📊</div>
                  <div className="document-info">
                    <h4>Financial Statements</h4>
                    <span className={`document-status ${submission.financialStatementsPath ? 'submitted' : 'missing'}`}>
                      {submission.financialStatementsPath ? '✅ Submitted' : '❌ Missing'}
                    </span>
                    {submission.financialStatementsPath && (
                      <button
                        onClick={() => setPreviewFile({
                          isOpen: true,
                          url: `${API_BASE}/admin/files/${submission.financialStatementsPath}`,
                          fileName: submission.financialStatementsPath?.split('/')?.pop() || 'Financial Statements',
                          contentType: 'application/pdf'
                        })}
                        className="document-link"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', font: 'inherit' }}
                      >
                        View
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="document-item">
                  <div className="document-icon">📄</div>
                  <div className="document-info">
                    <h4>Business Plan</h4>
                    <span className={`document-status ${submission.businessPlanPath ? 'submitted' : 'missing'}`}>
                      {submission.businessPlanPath ? '✅ Submitted' : '❌ Missing'}
                    </span>
                    {submission.businessPlanPath && (
                      <button
                        onClick={() => setPreviewFile({
                          isOpen: true,
                          url: `${API_BASE}/admin/files/${submission.businessPlanPath}`,
                          fileName: submission.businessPlanPath?.split('/')?.pop() || 'Business Plan',
                          contentType: 'application/pdf'
                        })}
                        className="document-link"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', font: 'inherit' }}
                      >
                        View
                      </button>
                    )}
                  </div>
                </div>

                {submission.otherDocumentsPath && (
                  <div className="document-item">
                    <div className="document-icon">📎</div>
                    <div className="document-info">
                      <h4>Other Documents</h4>
                      <span className="document-status submitted">✅ Submitted</span>
                      <button
                        onClick={() => setPreviewFile({
                          isOpen: true,
                          url: `${API_BASE}/admin/files/${submission.otherDocumentsPath}`,
                          fileName: submission.otherDocumentsPath?.split('/')?.pop() || 'Other Documents',
                          contentType: 'application/pdf'
                        })}
                        className="document-link"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', font: 'inherit' }}
                      >
                        View
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <div className="kyb-info-section">
              <h3 style={{ fontWeight: 800 }}>PEP & Sanctions Check</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label style={{ fontWeight: 700 }}>PEP Status</label>
                  <span className={submission.pep ? 'pep-status pep' : 'pep-status not-pep'}>
                    {submission.pep ? '⚠️ PEP Identified' : '✅ No PEP'}
                  </span>
                </div>
                <div className="info-item">
                  <label style={{ fontWeight: 700 }}>Sanctions Status</label>
                  <span className={submission.underSanctions ? 'sanctions-status sanctioned' : 'sanctions-status not-sanctioned'}>
                    {submission.underSanctions ? '⚠️ Sanctioned' : '✅ No Sanctions'}
                  </span>
                </div>
                {submission.pepDetails && submission.pepDetails.length > 0 && (
                  <div className="info-item full-width">
                    <label>PEP Details</label>
                    <div className="pep-details-list">
                      {submission.pepDetails.map((pep, index) => (
                        <div key={index} className="pep-detail-item">
                          <p><strong>Name:</strong> {pep.name}</p>
                          <p><strong>Position:</strong> {pep.position}</p>
                          <p><strong>Organization:</strong> {pep.organization}</p>
                          <p><strong>Country:</strong> {pep.country}</p>
                          <p><strong>Until:</strong> {pep.untilWhen}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {submission.sanctionType && (
                  <div className="info-item full-width">
                    <label>Sanctions Details</label>
                    <span>Type: {submission.sanctionType} - From: {submission.sanctionFrom}</span>
                  </div>
                )}
              </div>
            </div>
          </TabPanel>

          <TabPanel value={tabValue} index={5}>
            <div className="kyb-info-section">
              <h3 style={{ fontWeight: 800 }}>Review Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label style={{ fontWeight: 700 }}>Status</label>
                  <span className={getStatusBadgeClass(submission.status)}>
                    {submission.status}
                  </span>
                </div>
                <div className="info-item">
                  <label style={{ fontWeight: 700 }}>Reviewed By</label>
                  <span>{submission.reviewedBy || 'Not reviewed yet'}</span>
                </div>
                <div className="info-item">
                  <label style={{ fontWeight: 700 }}>Reviewed At</label>
                  <span>
                    {submission.reviewedAt ? formatDate(submission.reviewedAt) : 'Not reviewed yet'}
                  </span>
                </div>
                <div className="info-item">
                  <label style={{ fontWeight: 700 }}>Submitted At</label>
                  <span>
                    {submission.submittedAt ? formatDate(submission.submittedAt) : 'Not available'}
                  </span>
                </div>
                <div className="info-item full-width">
                  <label style={{ fontWeight: 700 }}>Comments</label>
                  <span>{submission.comments || 'No comments'}</span>
                </div>
              </div>
            </div>
          </TabPanel>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="action-btn secondary" onClick={onClose}>
            Close
          </button>
        </div>

        {/* File Preview Modal */}
        <FilePreviewModal
          isOpen={previewFile.isOpen}
          onClose={() => setPreviewFile({ isOpen: false, url: '', fileName: '', contentType: undefined })}
          fileUrl={previewFile.url}
          fileName={previewFile.fileName}
          contentType={previewFile.contentType}
        />
      </div>
    </div>
  );
};

export default KybDetailsModal;